/* ──────────────────────────────────────────────────────────────────────────
   Drive game — <drive-game> and <drive-game-popup>.

   <drive-game> is a canvas side-scroller. The swing is a double pendulum in
   the Bennett Foddy register: an arm hangs from a fixed shoulder and the club
   hangs off the end of it on a loose wrist. The pointer's x position never
   sets the club directly — it is the *target* for the arm, which a torque-
   limited spring pulls toward, so the arm lags and overshoots. The club is
   entirely passive: it cocks on the way back, lags on the way down, and only
   whips through if the player brakes their hands at the ball. Driving the
   hands straight through leaves the club trailing above the ball (a thin
   shot, or a whiff that simply isn't counted); braking too early casts it.
   Two things decide the shot:
     · strike — how close to the turf the clubhead is as it crosses the ball.
       It scales the ball speed and the launch angle, and outside a narrow
       band the club passes clean over the ball and the swing carries on.
     · clubhead speed through the ball, in club-lengths per second, which only
       a full backswing and a well-timed release produce.
   The physics runs in club-length units, so every canvas size plays the same.
   Ball flight is plain projectile motion in yards, then bounces, then rolls.
   The flight is *played back* faster than real time (timeScale) so a shot
   resolves in a few seconds — the physics and the distances are unchanged.
   The camera tracks the ball on x only; when the ball climbs out of the top
   of the frame a triangle at the top edge marks where it is.

   The world is drawn in a Tiny-Wings register: dawn sky, sun, parallax
   striped hills, a saturated fairway, and a gold zone painted on the grass
   from the target yardage onward so the goal is visible in the world itself.

   <drive-game-popup> is the shell: a small teaser pill first (never a
   takeover), then the modal with the game beside a locked signup panel that
   the win unlocks, then the code.

   DRIVE_TUNING below is the whole difficulty curve — those numbers are the dials.
   ────────────────────────────────────────────────────────────────────────── */

const DRIVE_TUNING = {
  skyYards: 22, // yards of air visible above the grass line
  gravity: 10.7, // yd/s2
  launchDeg: 21, // launch angle off the tee from a pure strike
  loftGain: 0.35, // degrees of launch lost per degree of forward shaft lean
  maxBallSpeed: 81.4, // yd/s at the reference clubhead speed and a pure strike
  speedRef: 17.5, // club-lengths/s of clubhead speed that earns maxBallSpeed
  speedCap: 1.1, // most of speedRef a freak swing can collect
  contactBand: 0.15, // club-lengths above the turf the head can still catch the ball
  restitution: 0.22, // vertical energy kept per bounce
  bounceGrip: 0.4, // horizontal speed kept per bounce
  rollFriction: 16, // yd/s2 once the ball is rolling
  teeHeight: 0.5, // yd from the turf to the ball's centre on the tee

  // The swing — a double pendulum, in club-lengths, seconds and radians.
  armRatio: 0.85, // arm length as a fraction of the club
  headMass: 0.3, // clubhead mass against the arm's 1 — lighter whips harder
  swingGravity: 14, // club-lengths/s2
  armStiffness: 55, // spring pulling the arm toward the pointer
  armDamping: 5,
  armTorque: 28, // cap on that spring — the arm cannot snap to the pointer
  wristDamping: 0.04, // the wrist is nearly free mid-swing
  wristHold: 0.6, // and firm the rest of the time, so the club settles
  wristLimit: 1.7, // rad of wrist hinge before it stiffens
  wristStiffness: 30,
  wristStop: 0.5, // damping against the hinge stop
  swingStep: 1 / 480, // s per physics substep
  maxSwing: 2.6, // rad — a full backswing for the arm, well past horizontal
  maxFollow: 2.4, // rad — how far through the arm can be pulled
  releaseTorque: 0.3, // share of armTorque that returns the hands to the ball when the pointer lets go
  swingTravel: 2.8, // full backswing in pointer-x pixels, as a multiple of the club
  swingStart: 0.05, // rad of arm travel back that starts a swing
  backswingTrigger: 0.9, // club-lengths behind the ball before a swing counts
  addressSlack: 0.3, // rad of aim either side of the ball that picks the club up
  thinStrike: 0.55, // strike below this earns the release hint
  timeScale: 1.7, // flight playback speed — distances are unaffected
  missResetMs: 1200, // how long a miss lingers before the tee resets
  winFreePlayMs: 2000, // celebration time before the tee resets for free play
};

const DRIVE_COLORS = {
  skyTop: '#ffdfa3',
  skyBottom: '#fff7e6',
  sun: '#ffc93c',
  sunRay: 'rgba(255, 201, 60, 0.35)',
  cloud: 'rgba(255, 255, 255, 0.85)',
  hillFar: '#fbc97e',
  hillFarStripe: '#fcd89f',
  hillNear: '#f49f63',
  hillNearStripe: '#f7b37e',
  grass: '#4ca648',
  grassStripe: '#5ab953',
  grassRim: '#a9e08b',
  goldZone: 'rgba(255, 201, 60, 0.32)',
  marker: '#fffdf5',
  markerText: '#2f7d3a',
  flag: '#ff5a2e',
  pole: '#fffdf5',
  shaft: '#6b543a',
  clubHead: '#2b2b28',
  arm: '#1f2d24',
  hand: '#efe9da',
  ballTrail: '255, 214, 120',
  confetti: ['#ffc93c', '#ff5a2e', '#5ab953', '#ffffff', '#f49f63'],
  dust: 'rgba(255, 240, 210, 0.9)',
};

// Where the club sits inside assets/driver.png (a 518×518 frame with
// transparent padding): the shaft centreline's x, and the grip-top → sole
// span, all in source pixels. The grip top is the swing pivot.
const DRIVE_CLUB_IMG = { size: 518, shaftX: 258, height: 514 };

const driveClamp = (value, min, max) => Math.min(Math.max(value, min), max);
const driveSeed = (i) => Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1;

class DriveGame extends HTMLElement {
  connectedCallback() {
    if (this.canvas) return;

    this.canvas = this.querySelector('[data-ref="canvas"]');
    this.ctx = this.canvas.getContext('2d');
    this.yardsEl = this.querySelector('[data-ref="yards"]');
    this.promptEl = this.querySelector('[data-ref="prompt"]');
    this.readoutEl = this.querySelector('[data-ref="readout"]');
    this.statusEl = this.querySelector('[data-ref="status"]');

    this.target = parseInt(this.dataset.target, 10) || 300;
    this.copy = {
      ready: this.dataset.promptReady || '',
      back: this.dataset.promptBack || '',
      miss: this.dataset.promptMiss || '',
      win: this.dataset.promptWin || '',
      pointer: this.dataset.promptPointer || '',
      hintSpeed: this.dataset.hintSpeed || '',
      hintBackswing: this.dataset.hintBackswing || '',
      hintClose: this.dataset.hintClose || '',
      hintStrike: this.dataset.hintStrike || '',
    };

    this.clubImg = null;
    if (this.dataset.clubSrc) {
      this.clubImg = new Image();
      this.clubImg.src = this.dataset.clubSrc;
      this.clubImg.addEventListener('load', () => {
        if (!this.running) this.render();
      });
    }

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.attempts = 0;
    this.trail = [];
    this.particles = [];
    this.rings = [];
    this.shake = 0;
    this.time = 0;
    this.running = false;
    this.hardReset();

    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.frame = this.frame.bind(this);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.canvas);

    this.canvas.addEventListener('focus', () => {
      if (this.canvas.matches(':focus-visible')) {
        this.announce(this.copy.pointer);
        this.dispatchEvent(new CustomEvent('drive-game:needs-pointer', { bubbles: true }));
      }
    });

    this.resize();
  }

  disconnectedCallback() {
    this.stop();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /* ---- lifecycle ---------------------------------------------------- */

  start() {
    if (this.running) return;
    this.running = true;
    this.resize();
    window.addEventListener('pointermove', this.handlePointerMove, { passive: true });
    window.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('pointercancel', this.handlePointerUp);
    this.lastFrame = performance.now() / 1000;
    this.raf = requestAnimationFrame(this.frame);
  }

  stop() {
    this.running = false;
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.resetTimer) clearTimeout(this.resetTimer);
  }

  hardReset() {
    this.attempts = 0;
    this.reset();
  }

  // A full reset puts the club back at rest on the ball and lets go of the
  // pointer: the arm only takes the pointer again once it is brought onto the
  // ball, so a pointer left out on the follow-through can't fling the club
  // around before the next swing. After a miss the club stays in hand
  // (keepClub) and only the ball goes back on the tee.
  reset(keepClub = false) {
    this.state = 'ready';
    if (!keepClub) {
      this.swing = { t1: 0, w1: 0, t2: 0, w2: 0 };
      this.aim = 0;
      this.engaged = false;
      this.released = false;
    }
    this.peak = 0;
    this.trail.length = 0;
    this.ball = { x: 0, y: DRIVE_TUNING.teeHeight, vx: 0, vy: 0, rolling: false };
    this.shot = null;
    this.setPrompt(this.engaged ? '' : this.copy.ready);
    this.setYards(0);
    if (this.readoutEl) this.readoutEl.hidden = true;
  }

  resize() {
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    this.rect = rect;
    this.w = rect.width;
    this.h = rect.height;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.groundY = this.h * 0.78;
    this.ppy = this.groundY / DRIVE_TUNING.skyYards; // px per yard
    // Arm plus club, fully extended, reaches from the shoulder to the turf.
    this.clubLen = this.h * 0.19;
    this.gameFont = (getComputedStyle(this).getPropertyValue('--game-font') || 'sans-serif').trim();
    if (!this.running) this.render();
  }

  /* ---- geometry ------------------------------------------------------ */

  get cameraX() {
    return this.ball.x;
  }

  sx(yards) {
    return this.w / 2 + (yards - this.cameraX) * this.ppy;
  }

  sy(yards) {
    return this.groundY - yards * this.ppy;
  }

  // The shoulder sits one arm-plus-club above the ball, so a fully extended
  // swing puts the head on it.
  shoulder() {
    return { x: this.sx(0), y: this.groundY - (1 + DRIVE_TUNING.armRatio) * this.clubLen };
  }

  hands() {
    const shoulder = this.shoulder();
    const arm = DRIVE_TUNING.armRatio * this.clubLen;
    return {
      x: shoulder.x - Math.sin(this.swing.t1) * arm,
      y: shoulder.y + Math.cos(this.swing.t1) * arm,
    };
  }

  // The clubhead relative to the ball, in club-lengths: x is negative behind
  // the ball, lift is how far above the turf it is, vx is its speed toward
  // the target. Angles are from hanging straight down, positive = swung back
  // (up and to the left), so x carries a minus sign.
  head() {
    const { t1, w1, t2, w2 } = this.swing;
    const arm = DRIVE_TUNING.armRatio;
    return {
      x: -(arm * Math.sin(t1) + Math.sin(t2)),
      lift: arm + 1 - (arm * Math.cos(t1) + Math.cos(t2)),
      vx: -(arm * Math.cos(t1) * w1 + Math.cos(t2) * w2),
    };
  }

  // Only the pointer's x counts, and only as a target for the arm: the
  // spring that chases it is what actually moves anything. Measured from the
  // frame's centre, where the golfer stands, rather than from the shoulder,
  // which slides away with the camera while the ball is in the air.
  aimFor(x) {
    const travel = this.clubLen * DRIVE_TUNING.swingTravel;
    const aim = ((this.w / 2 - x) / travel) * DRIVE_TUNING.maxSwing;
    return driveClamp(aim, -DRIVE_TUNING.maxFollow, DRIVE_TUNING.maxSwing);
  }

  /* ---- input --------------------------------------------------------- */

  handlePointerMove(event) {
    if (!this.rect) return;
    const x = event.clientX - this.rect.left;
    const y = event.clientY - this.rect.top;
    const aim = this.aimFor(x);

    if (!this.engaged) {
      const inside = x >= 0 && x <= this.rect.width && y >= 0 && y <= this.rect.height;
      // 'back' too: a released swing that whiffed is left mid-swing.
      const swinging = this.state === 'ready' || this.state === 'back';
      if (!swinging || !inside || Math.abs(aim) > DRIVE_TUNING.addressSlack) return;
      this.engaged = true;
      this.released = false;
    }
    this.aim = aim;
  }

  // Letting go mid-swing doesn't freeze the club where the pointer left it:
  // the hands return to the ball on a weaker shoulder (releaseTorque) and the
  // wrist whips the club through, so a loaded backswing can be released into
  // the ball without swinging forward. The deeper the backswing, the bigger
  // the shot. The club is picked up again the same way as at address.
  handlePointerUp() {
    if (!this.engaged) return;
    this.engaged = false;
    this.released = true;
    this.aim = 0;
  }

  /* ---- the swing ----------------------------------------------------- */

  // Angular accelerations of the arm (t1) and club (t2): the textbook double
  // pendulum with point masses at the hands and the head, solved through its
  // 2×2 mass matrix so the shoulder and wrist torques can go in directly.
  swingAccel(s) {
    const T = DRIVE_TUNING;
    const l1 = T.armRatio;
    const m1 = 1;
    const m2 = T.headMass;

    const torque = this.released ? T.armTorque * T.releaseTorque : T.armTorque;
    const shoulder = driveClamp(T.armStiffness * (this.aim - s.t1) - T.armDamping * s.w1, -torque, torque);

    const hinge = s.t2 - s.t1;
    const hingeRate = s.w2 - s.w1;
    // Loose only while a swing is on: at address and after the shot the
    // wrist firms up, so the club settles under the hands instead of flapping.
    const looseWrist = this.state === 'back';
    let wrist = -(looseWrist ? T.wristDamping : T.wristHold) * hingeRate;
    if (Math.abs(hinge) > T.wristLimit) {
      wrist -= T.wristStiffness * (hinge - Math.sign(hinge) * T.wristLimit) + T.wristStop * hingeRate;
    }

    const d = s.t1 - s.t2;
    const cos = Math.cos(d);
    const sin = Math.sin(d);
    const a = (m1 + m2) * l1 * l1;
    const b = m2 * l1 * cos;
    const c = m2;
    const r1 = shoulder - wrist - m2 * l1 * sin * s.w2 * s.w2 - (m1 + m2) * T.swingGravity * l1 * Math.sin(s.t1);
    const r2 = wrist + m2 * l1 * sin * s.w1 * s.w1 - m2 * T.swingGravity * Math.sin(s.t2);
    const det = a * c - b * b;
    return [(r1 * c - b * r2) / det, (a * r2 - b * r1) / det];
  }

  // Real time, not flight time: the swing has to feel like the hand.
  stepSwing(dt) {
    const s = this.swing;
    const h = DRIVE_TUNING.swingStep;
    let remaining = Math.min(dt, 0.05);

    while (remaining > 0) {
      const step = Math.min(h, remaining);
      remaining -= step;
      const before = this.head();
      const [a1, a2] = this.swingAccel(s);
      s.w1 += a1 * step;
      s.w2 += a2 * step;
      s.t1 += s.w1 * step;
      s.t2 += s.w2 * step;
      this.watchSwing(before, this.head());
    }
  }

  watchSwing(before, after) {
    switch (this.state) {
      // The swing — and the loose wrist — starts the moment the arm moves
      // back, but only counts once the head is well behind the ball. Easing
      // back to address before then just puts the club down again.
      case 'ready':
        if (this.swing.t1 > DRIVE_TUNING.swingStart) {
          this.state = 'back';
          this.loaded = false;
          this.peak = this.swing.t1;
        }
        break;

      case 'back':
        this.peak = Math.max(this.peak, this.swing.t1);
        if (!this.loaded) {
          if (after.x < -DRIVE_TUNING.backswingTrigger) {
            this.loaded = true;
            this.setPrompt(this.copy.back);
          } else if (this.swing.t1 < DRIVE_TUNING.swingStart / 2) {
            this.state = 'ready';
          }
        } else if (before.x < 0 && after.x >= 0 && after.vx > 0 && after.lift < DRIVE_TUNING.contactBand) {
          // A head that crosses above the contact band never touches the
          // ball — a whiff is just a whiff: nothing is recorded, the swing
          // carries on and the next pass through can still hit it.
          this.impact(after);
        }
        break;

      default:
        break;
    }
  }

  /* ---- the shot ------------------------------------------------------ */

  impact(head) {
    const T = DRIVE_TUNING;
    // Strike falls off with the square of the gap under the head, so a
    // millimetre thin costs little and the edge of the band barely moves it.
    const strike = Math.max(0, 1 - (head.lift / T.contactBand) ** 2);
    const load = driveClamp(this.peak / T.maxSwing, 0, 1);
    this.shot = { speed: head.vx, strike, load };

    const power = driveClamp(head.vx / T.speedRef, 0, T.speedCap);
    const speed = T.maxBallSpeed * power * (0.55 + 0.45 * strike);
    // Thin contact launches low, and so does a shaft leaning toward the
    // target (t2 > 0); a flipped, scooping wrist adds loft.
    const leanDeg = (this.swing.t2 * 180) / Math.PI;
    const launchDeg = driveClamp(T.launchDeg * (0.4 + 0.6 * strike) - leanDeg * T.loftGain, 6, 40);
    const launch = (launchDeg * Math.PI) / 180;

    this.ball.vx = speed * Math.cos(launch);
    this.ball.vy = speed * Math.sin(launch);
    this.ball.y = T.teeHeight;
    this.ball.rolling = false;
    this.state = 'flight';
    this.setPrompt('');

    // Impact juice — proportional to how hard the swing was.
    this.kick(power * 10);
    this.rings.push({ x: 0, y: T.teeHeight, r: 4, life: 1 });
    this.burst(0, T.teeHeight, Math.round(6 + power * 12), {
      speed: 6 + power * 12,
      up: true,
      colors: [DRIVE_COLORS.dust],
      size: 2.5,
    });
  }

  step(dt) {
    if (this.state !== 'flight') return;
    const ball = this.ball;
    const slice = 1 / 240;
    let remaining = Math.min(dt, 0.05) * DRIVE_TUNING.timeScale;

    while (remaining > 0) {
      const h = Math.min(slice, remaining);
      remaining -= h;

      if (ball.rolling) {
        ball.vx = Math.max(0, ball.vx - DRIVE_TUNING.rollFriction * h);
        ball.x += ball.vx * h;
        if (ball.vx < 0.4) {
          ball.vx = 0;
          this.finish();
          return;
        }
      } else {
        ball.vy -= DRIVE_TUNING.gravity * h;
        ball.x += ball.vx * h;
        ball.y += ball.vy * h;
        if (ball.y <= 0) {
          ball.y = 0;
          if (Math.abs(ball.vy) > 4) {
            this.kick(Math.min(Math.abs(ball.vy) * 0.35, 6));
            this.burst(ball.x, 0.15, 8, { speed: Math.abs(ball.vy) * 0.4, up: true, colors: [DRIVE_COLORS.dust], size: 2 });
            ball.vy = -ball.vy * DRIVE_TUNING.restitution;
            ball.vx *= DRIVE_TUNING.bounceGrip;
          } else {
            ball.vy = 0;
            ball.rolling = true;
          }
        }
      }
    }
  }

  finish() {
    const yards = Math.round(this.ball.x);
    this.attempts += 1;
    this.setYards(yards, true);

    if (yards >= this.target) {
      this.state = 'won';
      this.celebrate();
      this.setPrompt((this.copy.win || '[yards]!').replace('[yards]', yards));
      this.announce(`${yards} yards.`);
      this.dispatchEvent(new CustomEvent('drive-game:win', { bubbles: true, detail: { yards } }));
      this.resetTimer = setTimeout(() => {
        if (this.state === 'won') this.reset();
      }, DRIVE_TUNING.winFreePlayMs);
      return;
    }

    this.state = 'missed';
    this.setPrompt('');
    this.showReadout(yards);
    this.announce(`${yards} yards. Short of ${this.target}.`);
    this.dispatchEvent(
      new CustomEvent('drive-game:miss', { bubbles: true, detail: { yards, attempts: this.attempts } })
    );
    this.resetTimer = setTimeout(() => {
      if (this.state === 'missed') this.reset(true);
    }, DRIVE_TUNING.missResetMs);
  }

  /* ---- juice ---------------------------------------------------------- */

  kick(amount) {
    if (this.reducedMotion) return;
    this.shake = Math.max(this.shake, amount);
  }

  // World-space particles, in yards, so the camera carries them naturally.
  burst(x, y, count, { speed = 10, up = false, colors = DRIVE_COLORS.confetti, size = 3, life = 0.7 } = {}) {
    if (this.reducedMotion) count = Math.min(count, 4);
    for (let i = 0; i < count; i += 1) {
      const angle = up ? Math.PI * (0.15 + Math.random() * 0.7) : Math.random() * Math.PI * 2;
      const v = speed * (0.4 + Math.random() * 0.6);
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * v,
        vy: Math.sin(angle) * v,
        life: life * (0.6 + Math.random() * 0.8),
        age: 0,
        size: size * (0.6 + Math.random() * 0.8),
        color: colors[Math.floor(Math.random() * colors.length)],
        spin: (Math.random() - 0.5) * 12,
      });
    }
  }

  celebrate() {
    this.burst(this.ball.x, 1, 60, { speed: 20, up: true, size: 4, life: 1.4 });
    this.burst(this.ball.x - 6, 4, 30, { speed: 14, up: true, size: 3, life: 1.2 });
    this.kick(8);
  }

  updateJuice(dt) {
    this.shake *= Math.exp(-dt * 9);
    if (this.shake < 0.15) this.shake = 0;

    for (let i = this.particles.length - 1; i >= 0; i -= 1) {
      const p = this.particles[i];
      p.age += dt;
      if (p.age >= p.life) {
        this.particles.splice(i, 1);
        continue;
      }
      p.vy -= DRIVE_TUNING.gravity * 1.4 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.y < 0) {
        p.y = 0;
        p.vy = Math.abs(p.vy) * 0.3;
        p.vx *= 0.6;
      }
    }

    for (let i = this.rings.length - 1; i >= 0; i -= 1) {
      const ring = this.rings[i];
      ring.life -= dt * 2.4;
      ring.r += dt * 220;
      if (ring.life <= 0) this.rings.splice(i, 1);
    }
  }

  /* ---- readouts ------------------------------------------------------ */

  setYards(yards, landed = false) {
    if (!this.yardsEl) return;
    if (this.yardsEl.textContent !== String(yards)) this.yardsEl.textContent = yards;
    this.yardsEl.classList.toggle('is-hot', yards >= this.target);
    if (landed && !this.reducedMotion) {
      this.yardsEl.classList.remove('is-pop');
      // Force a reflow so the pop animation can retrigger on the next landing.
      void this.yardsEl.offsetWidth;
      this.yardsEl.classList.add('is-pop');
    }
  }

  setPrompt(text) {
    if (!this.promptEl) return;
    this.promptEl.textContent = text;
    this.promptEl.hidden = !text;
    if (text && this.readoutEl) this.readoutEl.hidden = true;
  }

  // Miss feedback in game terms: the distance plus what to fix — never the
  // raw speed and strike numbers, which read as debug output.
  showReadout(yards) {
    if (!this.readoutEl || !this.shot) return;
    const line = (this.copy.miss || '[yards] yd').replace('[yards]', yards);
    let hint = '';
    if (this.shot.strike < DRIVE_TUNING.thinStrike) hint = this.copy.hintStrike;
    else if (this.target - yards <= 25) hint = this.copy.hintClose;
    else if (this.shot.load < 0.85) hint = this.copy.hintBackswing;
    else hint = this.copy.hintSpeed;
    this.readoutEl.textContent = hint ? `${line} — ${hint}` : line;
    this.readoutEl.hidden = false;
    if (this.promptEl) this.promptEl.hidden = true;
  }

  announce(text) {
    if (this.statusEl && text) this.statusEl.textContent = text;
  }

  /* ---- loop and drawing ---------------------------------------------- */

  frame(timestamp) {
    if (!this.running) return;
    const now = timestamp / 1000;
    const dt = Math.min(now - this.lastFrame, 0.1);
    this.lastFrame = now;
    this.time += dt;
    // Re-measured every frame: the card animates in on open, so a rect cached
    // at open time maps the pointer to the wrong place for the first swing.
    this.rect = this.canvas.getBoundingClientRect();

    this.stepSwing(dt);
    this.step(dt);
    this.updateJuice(dt);

    if (this.state === 'flight') {
      this.setYards(Math.max(0, Math.round(this.ball.x)));
      this.trail.push({ x: this.ball.x, y: this.ball.y });
      if (this.trail.length > 22) this.trail.shift();
    }

    this.render();
    this.raf = requestAnimationFrame(this.frame);
  }

  render() {
    const ctx = this.ctx;
    if (!ctx || !this.w) return;
    const w = this.w;
    const h = this.h;
    const groundY = this.groundY;
    const cam = this.cameraX;

    ctx.save();
    if (this.shake > 0) {
      ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);
    }

    // Dawn sky
    const sky = ctx.createLinearGradient(0, 0, 0, groundY);
    sky.addColorStop(0, DRIVE_COLORS.skyTop);
    sky.addColorStop(1, DRIVE_COLORS.skyBottom);
    ctx.fillStyle = sky;
    ctx.fillRect(-8, -8, w + 16, groundY + 8);

    this.drawSun(ctx);
    this.drawClouds(ctx, cam, w);
    this.drawHills(ctx, cam, groundY, w, 0.22, 0.3, DRIVE_COLORS.hillFar, DRIVE_COLORS.hillFarStripe);
    this.drawHills(ctx, cam, groundY, w, 0.13, 0.55, DRIVE_COLORS.hillNear, DRIVE_COLORS.hillNearStripe);
    this.drawFairway(ctx, cam, groundY, w, h);
    this.drawMarkers(ctx, cam, groundY, w);
    this.drawTargetFlag(ctx, groundY);
    this.drawTee(ctx, groundY, w);
    this.drawClub(ctx, w);
    this.drawBall(ctx);
    this.drawParticles(ctx);
    this.drawAltitudeMarker(ctx);

    ctx.restore();
  }

  drawSun(ctx) {
    const x = this.w * 0.8;
    const y = this.h * 0.2;
    const r = this.h * 0.11;
    if (!this.reducedMotion) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(this.time * 0.12);
      ctx.fillStyle = DRIVE_COLORS.sunRay;
      for (let i = 0; i < 8; i += 1) {
        ctx.rotate(Math.PI / 4);
        ctx.beginPath();
        ctx.moveTo(r * 1.2, -r * 0.28);
        ctx.lineTo(r * 2.1, 0);
        ctx.lineTo(r * 1.2, r * 0.28);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }
    ctx.fillStyle = DRIVE_COLORS.sun;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  drawClouds(ctx, cam, w) {
    const shift = -cam * this.ppy * 0.12;
    const drift = this.reducedMotion ? 0 : this.time * 6;
    const spacing = Math.max(180, w * 0.4);
    ctx.fillStyle = DRIVE_COLORS.cloud;
    const start = Math.floor((-shift - drift - spacing) / spacing);
    for (let i = start; i * spacing + shift + drift < w + spacing; i += 1) {
      const seed = driveSeed(i);
      const x = i * spacing + shift + drift + seed * spacing * 0.5;
      const y = this.h * (0.1 + seed * 0.22);
      const s = this.h * (0.03 + seed * 0.03);
      ctx.beginPath();
      ctx.arc(x, y, s * 1.4, 0, Math.PI * 2);
      ctx.arc(x + s * 1.6, y + s * 0.3, s, 0, Math.PI * 2);
      ctx.arc(x - s * 1.5, y + s * 0.35, s * 0.9, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Rolling Tiny-Wings bumps: a sine ridge filled to the ground, then darker
  // vertical bands clipped to the same curve for the candy striping.
  drawHills(ctx, cam, groundY, w, height, parallax, color, stripe) {
    const shift = cam * this.ppy * parallax;
    const amp = this.h * height * 0.45;
    const base = this.h * height;
    const freq = (Math.PI * 2) / (w * 0.9);
    const ridge = (x) => groundY - base + Math.sin((x + shift) * freq) * amp - Math.cos((x + shift) * freq * 0.37) * amp * 0.5;

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(-8, groundY);
    for (let x = -8; x <= w + 8; x += 16) ctx.lineTo(x, ridge(x));
    ctx.lineTo(w + 8, groundY);
    ctx.closePath();
    ctx.fill();

    ctx.save();
    ctx.clip();
    ctx.fillStyle = stripe;
    const band = Math.max(26, w * 0.05);
    const first = Math.floor((-shift - band) / (band * 2)) * band * 2;
    for (let x = first; x - shift < w + band; x += band * 2) {
      ctx.fillRect(x - shift, 0, band, groundY);
    }
    ctx.restore();
  }

  drawFairway(ctx, cam, groundY, w, h) {
    ctx.fillStyle = DRIVE_COLORS.grass;
    ctx.fillRect(-8, groundY, w + 16, h - groundY + 8);

    // Mowing stripes, laid out in world space so they carry the sense of speed
    ctx.fillStyle = DRIVE_COLORS.grassStripe;
    const stripe = 12; // yards
    const spanYards = w / this.ppy;
    const first = Math.floor((cam - spanYards) / (stripe * 2)) * stripe * 2;
    for (let x = first; x < cam + spanYards; x += stripe * 2) {
      ctx.fillRect(this.sx(x), groundY, stripe * this.ppy, h - groundY);
    }

    // The money zone — gold wash on the grass from the target onward, so the
    // goal is a place in the world, not just a number in the corner.
    const zoneX = this.sx(this.target);
    if (zoneX < w + 16) {
      ctx.fillStyle = DRIVE_COLORS.goldZone;
      ctx.fillRect(Math.max(zoneX, -8), groundY, w + 16 - Math.max(zoneX, -8), h - groundY + 8);
    }

    // Bright rim where turf meets sky
    ctx.fillStyle = DRIVE_COLORS.grassRim;
    ctx.fillRect(-8, groundY - 2, w + 16, 3);
  }

  drawMarkers(ctx, cam, groundY, w) {
    const spanYards = w / this.ppy;
    const step = 50;
    const from = Math.max(0, Math.floor((cam - spanYards) / step) * step);
    const to = cam + spanYards;
    const size = Math.round(this.h * 0.042);
    ctx.font = `${size}px ${this.gameFont}`;
    ctx.textAlign = 'center';
    for (let yards = from; yards <= to; yards += step) {
      if (yards === 0) continue;
      const x = this.sx(yards);
      if (x < -60 || x > w + 60) continue;
      const boardW = size * 2.4;
      const boardH = size * 1.5;
      const y = groundY + (this.h - groundY) * 0.32;
      ctx.fillStyle = DRIVE_COLORS.marker;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x - boardW / 2, y - boardH / 2, boardW, boardH, boardH * 0.3);
      else ctx.rect(x - boardW / 2, y - boardH / 2, boardW, boardH);
      ctx.fill();
      ctx.fillStyle = yards >= this.target ? DRIVE_COLORS.flag : DRIVE_COLORS.markerText;
      ctx.fillText(String(yards), x, y + size * 0.36);
    }
  }

  drawTargetFlag(ctx, groundY) {
    const x = this.sx(this.target);
    if (x < -80 || x > this.w + 80) return;
    const poleH = this.h * 0.24;
    const wave = this.reducedMotion ? 0 : Math.sin(this.time * 5) * this.h * 0.012;

    ctx.strokeStyle = DRIVE_COLORS.pole;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, groundY);
    ctx.lineTo(x, groundY - poleH);
    ctx.stroke();

    ctx.fillStyle = DRIVE_COLORS.flag;
    ctx.beginPath();
    ctx.moveTo(x, groundY - poleH);
    ctx.quadraticCurveTo(
      x + this.h * 0.05,
      groundY - poleH + this.h * 0.01 + wave,
      x + this.h * 0.1,
      groundY - poleH + this.h * 0.035 + wave
    );
    ctx.lineTo(x, groundY - poleH + this.h * 0.07);
    ctx.closePath();
    ctx.fill();
  }

  drawTee(ctx, groundY, w) {
    const x = this.sx(0);
    if (x < -w * 0.4 || x > w * 1.4) return;
    ctx.fillStyle = DRIVE_COLORS.shaft;
    // The peg runs up to the ball's centre, so its top tucks under the ball.
    const peg = this.ppy * DRIVE_TUNING.teeHeight;
    ctx.fillRect(x - 1.5, groundY - peg, 3, peg);
  }

  // The arm, then the club hung off the hands — the driver artwork swung
  // about its grip, with the old vector shaft-and-disc as a fallback until
  // (or in case) the image loads.
  drawClub(ctx, w) {
    const px = this.sx(0);
    if (px < -w * 0.4 || px > w * 1.4) return;

    const phi = this.swing.t2;
    const shoulder = this.shoulder();
    const pivot = this.hands();

    ctx.strokeStyle = DRIVE_COLORS.arm;
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(5, this.clubLen * 0.12);
    ctx.beginPath();
    ctx.moveTo(shoulder.x, shoulder.y);
    ctx.lineTo(pivot.x, pivot.y);
    ctx.stroke();
    ctx.fillStyle = DRIVE_COLORS.arm;
    ctx.beginPath();
    ctx.arc(shoulder.x, shoulder.y, ctx.lineWidth * 0.9, 0, Math.PI * 2);
    ctx.fill();
    const handRadius = ctx.lineWidth * 0.62;

    this.drawDriver(ctx, pivot, phi);

    // Hands over the grip, so the wrist reads as the hinge it is.
    ctx.fillStyle = DRIVE_COLORS.hand;
    ctx.strokeStyle = DRIVE_COLORS.arm;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(pivot.x, pivot.y, handRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  drawDriver(ctx, pivot, phi) {
    if (this.clubImg && this.clubImg.complete && this.clubImg.naturalWidth) {
      // Grip top on the hands — clubLen covers the whole club, so at full
      // extension the sole sits on the turf.
      const scale = this.clubLen / DRIVE_CLUB_IMG.height;
      const size = DRIVE_CLUB_IMG.size * scale;
      ctx.save();
      ctx.translate(pivot.x, pivot.y);
      ctx.rotate(phi);
      ctx.drawImage(this.clubImg, -DRIVE_CLUB_IMG.shaftX * scale, 0, size, size);
      ctx.restore();
      return;
    }

    const radius = Math.max(4, this.clubLen * 0.15);
    // The shaft stops a head-radius short so the head sits on the turf at
    // address rather than sinking into it.
    const shaft = this.clubLen - radius * 0.9;
    const tipX = pivot.x - Math.sin(phi) * shaft;
    const tipY = pivot.y + Math.cos(phi) * shaft;

    ctx.strokeStyle = DRIVE_COLORS.shaft;
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(2, this.h * 0.011);
    ctx.beginPath();
    ctx.moveTo(pivot.x, pivot.y);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();

    // Head: a half-disc sitting just left of the shaft, flat face toward the
    // ball. Both the offset and the disc rotate with the club, so the face
    // stays square to the swing all the way through.
    ctx.save();
    ctx.translate(tipX - Math.cos(phi) * radius * 0.5, tipY - Math.sin(phi) * radius * 0.5);
    ctx.rotate(phi);
    ctx.fillStyle = DRIVE_COLORS.clubHead;
    ctx.beginPath();
    ctx.arc(0, 0, radius, Math.PI / 2, Math.PI * 1.5);
    ctx.closePath();
    ctx.fill();
    // Glint on the crown
    ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.beginPath();
    ctx.arc(-radius * 0.35, -radius * 0.3, radius * 0.22, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  drawBall(ctx) {
    const radius = Math.max(3.5, this.ppy * 0.36);

    // Comet trail
    this.trail.forEach((point, index) => {
      const alpha = (index / this.trail.length) * 0.55;
      ctx.fillStyle = `rgba(${DRIVE_COLORS.ballTrail}, ${alpha})`;
      ctx.beginPath();
      ctx.arc(this.sx(point.x), this.sy(point.y), radius * (0.25 + (index / this.trail.length) * 0.55), 0, Math.PI * 2);
      ctx.fill();
    });

    // Impact rings
    this.rings.forEach((ring) => {
      ctx.strokeStyle = `rgba(255, 255, 255, ${driveClamp(ring.life, 0, 1) * 0.8})`;
      ctx.lineWidth = 3 * ring.life;
      ctx.beginPath();
      ctx.arc(this.sx(ring.x), this.sy(ring.y), ring.r, 0, Math.PI * 2);
      ctx.stroke();
    });

    // Shadow on the turf, tightening as the ball drops
    const drop = driveClamp(1 - this.ball.y / 12, 0.15, 1);
    ctx.fillStyle = `rgba(31, 45, 36, ${0.28 * drop})`;
    ctx.beginPath();
    ctx.ellipse(this.sx(this.ball.x), this.groundY + 2, radius * 1.3 * drop, radius * 0.5 * drop, 0, 0, Math.PI * 2);
    ctx.fill();

    const y = this.sy(this.ball.y);
    if (y < -radius) return; // above the frame — the triangle stands in for it

    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = 'rgba(43, 43, 40, 0.65)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(this.sx(this.ball.x), y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Until the pointer picks the club up, a ring pulses on the ball to say
    // where to put it.
    if (this.state === 'ready' && !this.engaged) {
      const pulse = this.reducedMotion ? 0.5 : (Math.sin(this.time * 4) + 1) / 2;
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.55 + pulse * 0.4})`;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(this.sx(this.ball.x), y, radius * (2.4 + pulse * 0.8), 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  drawParticles(ctx) {
    this.particles.forEach((p) => {
      const fade = 1 - p.age / p.life;
      ctx.save();
      ctx.globalAlpha = driveClamp(fade, 0, 1);
      ctx.translate(this.sx(p.x), this.sy(p.y));
      ctx.rotate(p.age * p.spin);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.7);
      ctx.restore();
    });
  }

  // The camera never tilts, so a triangle at the top edge stands in for the
  // ball while it is above the frame — the higher it flies, the bigger it gets.
  drawAltitudeMarker(ctx) {
    const above = this.ball.y - DRIVE_TUNING.skyYards;
    if (above <= 0) return;
    const size = driveClamp(10 + above * 1.4, 10, 46);
    const x = this.sx(this.ball.x);
    const top = 6;
    ctx.fillStyle = DRIVE_COLORS.flag;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x + size * 0.55, top + size);
    ctx.lineTo(x - size * 0.55, top + size);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}

customElements.define('drive-game', DriveGame);

class DriveGamePopup extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;

    this.card = this.querySelector('[data-ref="card"]');
    this.game = this.querySelector('drive-game');
    this.steps = this.querySelectorAll('[data-step]');
    this.teaser = this.querySelector('[data-ref="teaser"]');
    this.dock = this.querySelector('[data-ref="dock"]');
    this.panel = this.querySelector('[data-ref="panel"]');
    this.skip = this.querySelector('[data-ref="skip"]');
    this.form = this.querySelector('[data-ref="form"]');
    this.storageKey = this.dataset.storageKey || 'golfbroidery:drive-game';
    this.mercy = parseInt(this.dataset.mercy, 10) || 0;
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.unlocked = false;

    this.handleKeyup = this.handleKeyup.bind(this);

    this.querySelectorAll('[data-ref="close"]').forEach((button) =>
      button.addEventListener('click', () => this.close('dismissed'))
    );

    // Teaser: play opens the game, the little × dismisses for the visit.
    if (this.teaser) {
      this.teaser.querySelector('[data-ref="teaser-play"]').addEventListener('click', () => {
        this.hideTeaser();
        this.open('play');
      });
      this.teaser.querySelector('[data-ref="teaser-close"]').addEventListener('click', (event) => {
        event.stopPropagation();
        this.hideTeaser();
        if (this.read().state !== 'joined') this.write({ state: 'dismissed' });
        // Dismissed, not gone — tuck away as the docked half golf ball.
        this.showDock();
      });
    }

    if (this.dock) {
      this.dock.addEventListener('click', () => {
        this.hideDock();
        this.open('play');
      });
    }

    this.addEventListener('drive-game:win', (event) => this.unlock(event.detail.yards));
    this.addEventListener('drive-game:miss', (event) => {
      if (this.mercy && event.detail.attempts >= this.mercy) this.revealSkip();
    });
    this.addEventListener('drive-game:needs-pointer', () => this.revealSkip());

    if (this.skip) {
      this.skip.querySelector('button').addEventListener('click', () => this.unlock(null));
    }

    // The form posts natively (Shopify spam protection breaks a fetch submit),
    // so the step — and where the visitor was on the page — is stashed before
    // the reload and picked up again on return.
    if (this.form) {
      this.form.addEventListener('submit', () =>
        this.write({ state: 'joined', pending: true, scrollY: window.scrollY })
      );
    }

    const copyButton = this.querySelector('[data-ref="copy"]');
    if (copyButton) {
      copyButton.addEventListener('click', () => {
        const code = this.querySelector('[data-ref="code"]');
        if (!code || !navigator.clipboard) return;
        navigator.clipboard.writeText(code.textContent.trim()).then(() => {
          copyButton.textContent = copyButton.dataset.copiedLabel || copyButton.textContent;
        });
      });
    }

    this.watchKeyboard();
    this.setLocked(true);
    this.restore();
  }

  // On a phone the card is centred in the layout viewport and as tall as the
  // screen, and the keyboard doesn't change either, so it opened straight over
  // the email field. While a field has focus the card is fitted to the visual
  // viewport (the part the keyboard leaves) and scrolled until the form shows.
  watchKeyboard() {
    const vv = window.visualViewport;
    if (!vv || !this.card) return;
    let field = null;

    const fit = () => {
      if (!field) return;
      const keyboardUp = vv.height < window.innerHeight * 0.8;
      this.card.classList.toggle('is-keyboard', keyboardUp);
      this.card.style.setProperty('--dg-vv-top', `${vv.offsetTop}px`);
      this.card.style.setProperty('--dg-vv-height', `${vv.height}px`);
      if (!keyboardUp) return;
      requestAnimationFrame(() => {
        if (!field) return;
        const target = field.closest('form') || field;
        const box = this.card.getBoundingClientRect();
        const rect = target.getBoundingClientRect();
        if (rect.top >= box.top + 12 && rect.bottom <= box.bottom - 12) return;
        this.card.scrollTop += rect.top - box.top - Math.max(12, (box.height - rect.height) / 2);
      });
    };

    this.card.addEventListener('focusin', (event) => {
      if (!event.target.matches('input:not([type="hidden"]), textarea')) return;
      field = event.target;
      vv.addEventListener('resize', fit);
      vv.addEventListener('scroll', fit);
      fit();
    });
    this.card.addEventListener('focusout', () => {
      field = null;
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
      this.card.classList.remove('is-keyboard');
    });
  }

  // The theme editor swaps sections out live; do not leave the page scroll
  // locked behind a popup that no longer exists.
  disconnectedCallback() {
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keyup', this.handleKeyup);
    if (this.game) this.game.stop();
  }

  /* ---- when to appear ------------------------------------------------ */

  restore() {
    const stored = this.read();

    // Coming back from a form post. Which step to reopen comes from storage,
    // not from Liquid: form.posted_successfully? is true for every customer
    // form on the page, so the footer newsletter would otherwise hand out the
    // code as well.
    if (stored.pending) {
      const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
      this.write({ state: 'joined' });
      this.returnToScroll(stored.scrollY || 0);
      if (posted) {
        this.open('thanks');
        return;
      }
      if (this.querySelector('[data-ref="form-error"]')) {
        this.open('play');
        this.unlock(null);
        return;
      }
    }

    if (this.designMode) {
      setTimeout(() => this.open('play'), 300);
      return;
    }

    if (this.dataset.enabled !== 'true') return;

    if (!this.shouldShow(stored)) {
      // A visitor who dismissed it keeps the quiet docked ball instead of the
      // teaser — still one click from the game, never in the way. Signed-up
      // visitors get nothing at all.
      if (stored.state === 'dismissed') this.showDock();
      return;
    }

    // Never a takeover: after the delay only the small teaser appears — the
    // game opens when the visitor asks for it.
    const delay = (parseFloat(this.dataset.delay) || 0) * 1000;
    setTimeout(() => this.showTeaser(), delay);
  }

  // Shopify sends the post back to ?customer_posted=true#DriveGameSignup, and
  // the browser jumps to that anchor — the popup's markup at the foot of the
  // page. Drop the anchor and put the visitor back where they signed up.
  returnToScroll(y) {
    const url = new URL(window.location.href);
    url.searchParams.delete('customer_posted');
    url.hash = '';
    history.replaceState(history.state, '', url.pathname + url.search);
    window.scrollTo(0, y);
    // The anchor jump can land after this runs; undo it once more on load.
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => window.scrollTo(0, y), { once: true });
    }
  }

  shouldShow(stored) {
    if (stored.state === 'joined') return false;
    if (stored.state === 'dismissed') {
      const days = parseFloat(this.dataset.days) || 0;
      if (!days) return false;
      return (Date.now() - (stored.at || 0)) / 86400000 >= days;
    }
    return true;
  }

  read() {
    try {
      return JSON.parse(window.localStorage.getItem(this.storageKey)) || {};
    } catch (error) {
      return {};
    }
  }

  write(value) {
    if (this.designMode) return;
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify({ ...value, at: Date.now() }));
    } catch (error) {
      // Private browsing — the popup simply comes back next visit.
    }
  }

  /* ---- teaser ---------------------------------------------------------- */

  showTeaser() {
    if (!this.teaser || this.hasAttribute('open')) return;
    this.teaser.hidden = false;
    // Two frames so the transition runs from the hidden state.
    requestAnimationFrame(() => requestAnimationFrame(() => this.teaser.classList.add('is-in')));
  }

  hideTeaser() {
    if (!this.teaser) return;
    this.teaser.classList.remove('is-in');
    this.teaser.hidden = true;
  }

  /* ---- docked ball ------------------------------------------------------ */

  showDock() {
    if (!this.dock || this.hasAttribute('open')) return;
    if (this.read().state === 'joined') return;
    this.dock.hidden = false;
    // Two frames so the roll-in transition runs from the hidden state.
    requestAnimationFrame(() => requestAnimationFrame(() => this.dock.classList.add('is-in')));
  }

  hideDock() {
    if (!this.dock) return;
    this.dock.classList.remove('is-in');
    this.dock.hidden = true;
  }

  /* ---- open and close ------------------------------------------------ */

  open(step) {
    if (this.hasAttribute('open')) {
      this.showStep(step);
      return;
    }
    this.setAttribute('open', '');
    this.hideTeaser();
    this.hideDock();
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keyup', this.handleKeyup);
    this.showStep(step);
    if (typeof trapFocus === 'function') trapFocus(this.card, this.card);
  }

  close(reason) {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keyup', this.handleKeyup);
    if (this.game) this.game.stop();
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    if (reason === 'dismissed' && this.read().state !== 'joined') this.write({ state: 'dismissed' });
    // In the theme editor the merchant should be able to see both states:
    // closing the modal brings the teaser up.
    if (this.designMode) {
      this.showTeaser();
      return;
    }
    // Closing without signing up docks the game to the left edge rather than
    // losing it — the half ball is the way back in.
    if (this.read().state !== 'joined') this.showDock();
  }

  handleKeyup(event) {
    if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close('dismissed');
  }

  showStep(name) {
    this.steps.forEach((step) => {
      step.hidden = step.dataset.step !== name;
    });

    if (name === 'play') {
      if (this.game) {
        this.game.hardReset();
        this.game.start();
      }
      return;
    }

    if (this.game) this.game.stop();
  }

  /* ---- the locked panel ------------------------------------------------ */

  setLocked(locked) {
    if (!this.panel) return;
    this.panel.classList.toggle('is-locked', locked);
    // Only the form controls lock — the skip link must stay usable while the
    // panel is still greyed out.
    this.panel.querySelectorAll('.drive-game-popup__form input, .drive-game-popup__form button').forEach((el) => {
      el.disabled = locked;
    });
  }

  // The win. The greyed form comes alive; the game keeps running behind it so
  // the confetti and the free-play tee stay on screen.
  unlock(yards) {
    if (this.unlocked) {
      if (yards) this.stampScore(yards);
      return;
    }
    this.unlocked = true;
    this.setLocked(false);
    this.panel.classList.add('is-unlocked');

    const lockedCopy = this.querySelector('[data-panel="locked"]');
    const unlockedCopy = this.querySelector('[data-panel="unlocked"]');
    if (lockedCopy) lockedCopy.hidden = true;
    if (unlockedCopy) unlockedCopy.hidden = false;
    if (this.skip) this.skip.hidden = true;
    if (yards) this.stampScore(yards);

    const input = this.querySelector('[data-ref="email"]');
    if (input) setTimeout(() => input.focus({ preventScroll: true }), 450);
  }

  stampScore(yards) {
    const score = this.querySelector('[data-ref="score"]');
    const scoreValue = this.querySelector('[data-ref="score-value"]');
    if (score) score.hidden = false;
    if (scoreValue) scoreValue.textContent = yards;
  }

  revealSkip() {
    if (this.skip && !this.unlocked) this.skip.hidden = false;
  }
}

customElements.define('drive-game-popup', DriveGamePopup);
